/**
 * Ceiling lighting.
 *
 * Fixtures are placed by a smooth noise field rather than evenly, so a level
 * has lit districts and unlit ones. The dark stretches are not a failure to
 * light the map — they are the reason the flashlight exists.
 */

import { TAU } from '../../core/math';
import { Cell, type ExitPoint, type LightSpot } from '../../core/types';
import { fbm2 } from './noise';
import type { GenSite } from './site';

/**
 * Hang a working fixture over every exit.
 *
 * The exit is the only objective the game has, and the generator deliberately
 * puts it in the far quartile of the level. Without this it is an unmarked
 * point on an identical floor: the one light still burning at the end of a
 * corridor is what makes it findable from past the fog line, and it is never
 * broken because a dead bulb over the way out is just cruelty.
 */
export function lightExits(site: GenSite, lights: LightSpot[], exits: readonly ExitPoint[]): void {
  const { grid, rng } = site;
  const taken = new Set<number>();
  for (const l of lights) {
    const c = grid.worldToCell(l.pos.x, l.pos.z);
    taken.add(c.cy * grid.w + c.cx);
  }
  for (const exit of exits) {
    const c = grid.worldToCell(exit.pos.x, exit.pos.z);
    const i = c.cy * grid.w + c.cx;
    if (taken.has(i)) continue;
    // Open has no ceiling to hang from; the rest cannot hold a fixture at all.
    if (!canHoldFixture(grid.cells[i])) continue;
    taken.add(i);
    lights.push({ pos: grid.cellToWorld(c.cx, c.cy), state: 'on', phase: rng.range(0, TAU) });
  }
}

/** Cells around a slide searched for somewhere to hang a fixture. */
const SLIDE_LIGHT_RADIUS = 4;
/** Working fixtures a slide ends up under, counting any that were already there. */
const SLIDE_LIGHT_COUNT = 3;

/**
 * Light every slide properly, which means more than lighting an exit.
 *
 * An exit gets one working fixture so it can be recognised once you are
 * standing at it. A slide has to be recognised from the far side of a room you
 * have not entered yet, so it gets a pool of them — every fixture already
 * within a few cells is repaired to `'on'`, and more are added until there are
 * three. On a level with any fog at all that reads as a bright patch on the
 * wall from well past the point the object itself resolves, which is the whole
 * job: the player should walk toward it before they know what it is.
 *
 * There are several slides on a level now, and this runs over all of them: the
 * fourth one is the one somebody walks into from the far corner of the map, and
 * an unlit mouth out there is a slide that is not on the level as far as that
 * player is concerned. The cost is a handful of extra fixtures against the
 * thousand or so a level already carries.
 *
 * Cells with no ceiling are skipped, so a slide standing outdoors on Level 94
 * gets nothing here. That level's ambient is daylight and everything on it is
 * already lit; hanging a fixture in mid-air over a lawn would be worse.
 */
export function lightSlides(site: GenSite, lights: LightSpot[], exits: readonly ExitPoint[]): void {
  const { grid, rng } = site;
  for (const exit of exits) {
    if (exit.kind !== 'slide') continue;
    const c = grid.worldToCell(exit.pos.x, exit.pos.z);

    const taken = new Set<number>();
    let working = 0;
    for (const l of lights) {
      const lc = grid.worldToCell(l.pos.x, l.pos.z);
      taken.add(lc.cy * grid.w + lc.cx);
      if (Math.abs(lc.cx - c.cx) > SLIDE_LIGHT_RADIUS || Math.abs(lc.cy - c.cy) > SLIDE_LIGHT_RADIUS) {
        continue;
      }
      // A dead bulb over the way out is cruelty; over the signposted way out it
      // is a bug report.
      l.state = 'on';
      working++;
    }

    // Spiral out from the mouth so the fixtures we add are the closest ones
    // available, and the pool is centred on the object rather than beside it.
    for (let radius = 0; radius <= SLIDE_LIGHT_RADIUS && working < SLIDE_LIGHT_COUNT; radius++) {
      for (let dy = -radius; dy <= radius && working < SLIDE_LIGHT_COUNT; dy++) {
        for (let dx = -radius; dx <= radius && working < SLIDE_LIGHT_COUNT; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) !== radius) continue;
          const x = c.cx + dx;
          const y = c.cy + dy;
          if (!grid.inBounds(x, y)) continue;
          const i = y * grid.w + x;
          if (taken.has(i) || !canHoldFixture(grid.cells[i])) continue;
          taken.add(i);
          lights.push({ pos: grid.cellToWorld(x, y), state: 'on', phase: rng.range(0, TAU) });
          working++;
        }
      }
    }
  }
}

/** Whether a cell value has a ceiling to hang a fixture from. */
function canHoldFixture(cell: number | undefined): boolean {
  return (
    cell !== undefined &&
    cell !== Cell.Void &&
    cell !== Cell.Wall &&
    cell !== Cell.Pillar &&
    cell !== Cell.Open
  );
}

/** Roughly one fixture per `spec.lightSpacing` walkable cells, clumped. */
export function placeLights(site: GenSite, walk: Int32Array, regionAt: Int32Array): LightSpot[] {
  const { grid, rng, spec, regions } = site;
  const target = Math.max(1, Math.round(walk.length / Math.max(1, spec.lightSpacing)));
  const step = Math.max(2, Math.min(6, Math.round(Math.sqrt(spec.lightSpacing / 3))));
  const fieldSeed = rng.int(0, 0xffff);
  const scale = 1 / 13;

  // One candidate per step-by-step block rather than a fixed lattice: a maze on
  // odd cells has no cells at all on an even lattice, and a whole level going
  // dark on a parity coincidence is not the kind of randomness we want.
  const cells: number[] = [];
  const scores: number[] = [];
  for (let by = 1; by < grid.h - 1; by += step) {
    for (let bx = 1; bx < grid.w - 1; bx += step) {
      let best = -1;
      let bestScore = -Infinity;
      const y1 = Math.min(by + step, grid.h - 1);
      const x1 = Math.min(bx + step, grid.w - 1);
      for (let y = by; y < y1; y++) {
        for (let x = bx; x < x1; x++) {
          const i = y * grid.w + x;
          const c = grid.cells[i]!;
          // A pillar fills the middle of its cell; Open has no ceiling to hang from.
          if (c === Cell.Void || c === Cell.Wall || c === Cell.Pillar || c === Cell.Open) continue;
          let s = fbm2(x * scale, y * scale, fieldSeed);
          const rid = regionAt[i]!;
          if (rid >= 0) {
            const kind = regions[rid]!.kind;
            // Corridors and open halls keep their strip lighting; warrens rarely do.
            if (kind === 'hall' || kind === 'pillar' || kind === 'atrium') s += 0.22;
            else if (kind === 'warren' || kind === 'closet') s -= 0.18;
          }
          // Nudge toward the middle of the block so fixtures stay roughly regular.
          s -= 0.02 * (Math.abs(x - bx - step / 2) + Math.abs(y - by - step / 2));
          if (s > bestScore) {
            bestScore = s;
            best = i;
          }
        }
      }
      if (best < 0) continue;
      cells.push(best);
      scores.push(bestScore + rng.range(-0.05, 0.05));
    }
  }
  if (cells.length === 0) return [];

  const order = cells.map((_, k) => k);
  order.sort((a, b) => scores[b]! - scores[a]!);
  const keep = Math.min(order.length, target);

  const lights: LightSpot[] = [];
  for (let n = 0; n < keep; n++) {
    const i = cells[order[n]!]!;
    const broken = rng.chance(spec.brokenLightChance);
    lights.push({
      pos: grid.cellToWorld(i % grid.w, (i / grid.w) | 0),
      state: broken ? (rng.chance(0.6) ? 'flicker' : 'dead') : 'on',
      phase: rng.range(0, TAU),
    });
  }
  return lights;
}
